import React, { useContext } from 'react';
import Select, { OnChangeValue } from 'react-select';
import { t, Trans } from '@lingui/macro';
import { DispatchContext, StateContext } from '../state/contexts';
import { OrgUnit } from '../jobs/types/job-opening';
import Job from '../jobs/types/job';
import Option from './types/option';
import filterAvailable from './services/filter-available';
import useOptions from './hooks/use-options';
import MatchedOption from './components/matched-option';
import useFilterSetup from './hooks/use-filter-setup';
import filterBy from './services/filter-by';
import './filters.scss';
import Properties from './types/properties';
import NoOptionsMessage from './components/no-options-message';
import MultiValueRemove from './components/multi-value-remove';

const id = (orgUnit: OrgUnit) => orgUnit.id;

const getAvailable = (jobs: Job[], allowed: string[]): OrgUnit[] => {
  const mapped = jobs.map((job) => job.jobOpening.orgUnit);
  return filterAvailable(mapped, allowed, id);
};

const filter = (jobs: Job[], selected: string[]): Job[] => {
  return selected.length > 0 ? jobs.filter((job) => selected.includes(id(job.jobOpening.orgUnit))) : jobs;
};

const OrgUnitsFilter = ({ hideLabel, showColon = false, defaultLabel, className = '' }: Properties) => {
  const state = useContext(StateContext);
  const dispatch = useContext(DispatchContext);

  const { config, filters } = state;
  const { orgUnits } = config.config.filters;
  const { showLabels } = config.config.ui;
  const { enabled, hidden, order, label } = orgUnits;

  useFilterSetup('orgUnits', filter, getAvailable);

  const options = useOptions('orgUnits', id);
  const value: Option[] = options.filter((option) => filters.orgUnits.selected.includes(option.value));

  const publish = (changedValue: OnChangeValue<Option, true>) => {
    const selected = changedValue.map((option) => option.value);
    dispatch(filterBy('orgUnits', selected));
  };

  return enabled && !hidden ? (
    <div className={`dvinci-job-filter ${className}`} style={{ order }}>
      {showLabels && !hideLabel && (
        <>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="dvinci-filter-heading h4" htmlFor="orgUnits">
            {label || defaultLabel || <Trans>Organization Units</Trans>}
            {showColon ? ':' : ''}
          </label>
        </>
      )}
      <div className="dvinci-select-wrapper">
        <Select
          className="dvinci-select-container"
          classNamePrefix="dvinci-select"
          components={{ Option: MatchedOption, NoOptionsMessage, MultiValueRemove }}
          inputId="orgUnits"
          placeholder={!(showLabels && !hideLabel) ? label || defaultLabel || t`Organization Units` : t`Please choose`}
          options={options}
          value={value}
          isMulti
          onChange={publish}
        />
      </div>
    </div>
  ) : (
    <></>
  );
};

export default OrgUnitsFilter;
